import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Outlet, useNavigate, Link as RouterLink } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import ButtonBase from '@mui/material/ButtonBase';
import Tooltip from '@mui/material/Tooltip';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import NavList from '../../components/NavList';
import { APP_BAR_TITLE_SX } from '../../components/appBarTitle';
import AppBarLogo from '../../components/AppBarLogo';
import ProfileMenu from '../../components/ProfileMenu';
import SyncStatusButton from '../../components/SyncStatusButton';
import { useAbility } from '../auth/abilityContext';
import { ProjectLabel } from '../projects/ProjectLabel';
import RollbackNotice from '../../components/RollbackNotice';
import { rememberApp } from '../../utils/lastApp';
import { FIELD_PAGE_PADDING, FIELD_PAGE_MAX_WIDTH } from './fieldPageMetrics';
import {
    fieldPickerPath,
    useFieldPickerOrigin
} from './useRestoreFieldProject';
import { useFieldRoute } from './useFieldRoute';
import { useActiveFieldProject } from './useActiveFieldProject';
import { useFieldNavGroups } from './useFieldNavGroups';
import { useFieldDocumentTitle } from './useFieldDocumentTitle';

const DRAWER_WIDTH = 290;

/**
 * Minimal, touch-first chrome for the outdoor field-recording tool —
 * deliberately separate from the office Layout's permanent drawer. The menu
 * reaches every level, including the open Befund's Planums/Profils, so nothing
 * is a dead end. An editor is always left by NAVIGATING, which unmounts it —
 * and that is what commits its autosave.
 */
export default function FieldLayout() {
    const intl = useIntl();
    const theme = useTheme();
    const navigate = useNavigate();
    const ability = useAbility();

    const route = useFieldRoute();
    const activeProject = useActiveFieldProject(route.projectId);
    const navGroups = useFieldNavGroups(route, ability);
    const pickerOrigin = useFieldPickerOrigin(ability);
    useFieldDocumentTitle(route, activeProject);

    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
    // Same logic as the office Layout: open on desktop, hidden on mobile,
    // re-synced when the breakpoint is crossed.
    const [drawerOpen, setDrawerOpen] = useState(isDesktop);
    // Adjusted while rendering rather than from an effect: crossing the
    // breakpoint would otherwise paint the drawer in its old state first.
    const [wasDesktop, setWasDesktop] = useState(isDesktop);
    if (wasDesktop !== isDesktop) {
        setWasDesktop(isDesktop);
        setDrawerOpen(isDesktop);
    }

    useEffect(() => {
        rememberApp('field');
    }, []);

    function navTo(to: string) {
        navigate(to);
        if (!isDesktop) {
            setDrawerOpen(false);
        }
    }

    const drawerContent = (
        <>
            {/* Spacer so the nav clears the fixed AppBar. */}
            <Toolbar />
            <Box sx={{ overflow: 'auto' }}>
                <NavList
                    groups={navGroups}
                    pathname={route.pathname}
                    onNavigate={navTo}
                />
            </Box>
        </>
    );

    return (
        <Box
            sx={{
                /**
                 * Exactly one screen tall, so a page can claim the space under
                 * the app bar with `flex: 1`. `dvh`, not `vh`: on a phone `vh`
                 * ignores the collapsing browser chrome and overflows by its
                 * height. `height`, not `min-height`: a minimum is not a
                 * DEFINITE height, so a `flex: 1` child would still grow past
                 * the screen and a grid inside it would virtualise nothing.
                 */
                height: '100dvh',
                overflow: 'hidden',
                display: 'flex',
                bgcolor: 'background.default'
            }}
        >
            <AppBar
                position="fixed"
                sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
            >
                <Toolbar>
                    {route.projectId ? (
                        <IconButton
                            color="inherit"
                            edge="start"
                            aria-label={intl.formatMessage({
                                id: 'layout.menu'
                            })}
                            onClick={() => setDrawerOpen((prev) => !prev)}
                            sx={{ mr: 1 }}
                        >
                            <MenuIcon />
                        </IconButton>
                    ) : (
                        // No project to draw a menu for, so the slot carries
                        // the way back — a standalone PWA can't be relied on
                        // for a back gesture.
                        pickerOrigin && (
                            <IconButton
                                color="inherit"
                                edge="start"
                                aria-label={intl.formatMessage({
                                    id: 'common.back'
                                })}
                                onClick={() =>
                                    navigate(pickerOrigin.path, {
                                        replace: true
                                    })
                                }
                                sx={{ mr: 1 }}
                            >
                                <ArrowBackIcon />
                            </IconButton>
                        )
                    )}
                    <AppBarLogo sx={{ mr: 1.5 }} />
                    {activeProject ? (
                        <ButtonBase
                            onClick={() =>
                                navigate(
                                    fieldPickerPath({
                                        projectId: route.projectId,
                                        category: route.category
                                    })
                                )
                            }
                            sx={{
                                flexGrow: 1,
                                minWidth: 0,
                                // Centred while the bar is narrow and this is
                                // the only thing on it; left once the mark
                                // stands in front of it.
                                justifyContent: {
                                    xs: 'center',
                                    md: 'flex-start'
                                },
                                textAlign: { xs: 'center', md: 'left' },
                                borderRadius: 1,
                                px: 0.5,
                                py: 0.25
                            }}
                        >
                            <Typography
                                variant="h6"
                                noWrap
                                sx={{ ...APP_BAR_TITLE_SX, minWidth: 0 }}
                            >
                                <ProjectLabel
                                    project={activeProject}
                                    compactBelow="sm"
                                />
                            </Typography>
                            <UnfoldMoreIcon
                                fontSize="small"
                                sx={{ ml: 0.5, flexShrink: 0, opacity: 0.8 }}
                            />
                        </ButtonBase>
                    ) : (
                        <Typography
                            variant="h6"
                            noWrap
                            sx={{
                                ...APP_BAR_TITLE_SX,
                                flexGrow: 1,
                                textAlign: { xs: 'center', md: 'left' }
                            }}
                        >
                            <FormattedMessage id="field.title" />
                        </Typography>
                    )}
                    {ability.can('access', 'Office') && (
                        <Tooltip
                            title={<FormattedMessage id="field.toOffice" />}
                        >
                            <IconButton
                                color="inherit"
                                aria-label="office"
                                component={RouterLink}
                                to="/office"
                                sx={{ ml: 0.5 }}
                            >
                                <DesktopWindowsIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                    {/* The office chrome's own button, so the two cannot tell
                        different stories about the same queue. */}
                    <SyncStatusButton />
                    <ProfileMenu />
                </Toolbar>
            </AppBar>

            {/* Desktop: permanent, toggleable. Mobile: temporary overlay. Only
                with a project open — that is what the menu navigates within. */}
            {route.projectId && isDesktop && drawerOpen && (
                <Drawer
                    variant="permanent"
                    sx={{
                        width: DRAWER_WIDTH,
                        flexShrink: 0,
                        '& .MuiDrawer-paper': {
                            width: DRAWER_WIDTH,
                            boxSizing: 'border-box'
                        }
                    }}
                >
                    {drawerContent}
                </Drawer>
            )}
            {route.projectId && !isDesktop && (
                <Drawer
                    variant="temporary"
                    open={drawerOpen}
                    onClose={() => setDrawerOpen(false)}
                    ModalProps={{ keepMounted: true }}
                    sx={{
                        '& .MuiDrawer-paper': {
                            width: DRAWER_WIDTH,
                            boxSizing: 'border-box'
                        }
                    }}
                >
                    {drawerContent}
                </Drawer>
            )}

            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                <Toolbar />
                <Box
                    sx={{
                        p: FIELD_PAGE_PADDING,
                        maxWidth: FIELD_PAGE_MAX_WIDTH,
                        width: '100%',
                        mx: 'auto',
                        // Fill the space under the app bar and pass it on; this
                        // is the field tool's scroller, the root being capped
                        // at one screen.
                        flex: 1,
                        minHeight: 0,
                        overflow: 'auto',
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    <Outlet />
                </Box>
            </Box>

            <RollbackNotice />
        </Box>
    );
}
